import gql from 'graphql-tag';
import { clients } from '../../../util/clients';
// import { getFYList } from '../../../util/shared/tmc-global';
import { fieldsToGqlString, mergeByIdNewAtTop } from '../../../util/shared/vue-global';

const { backend } = clients.direct;

const defaultTransactionFields = {
  _id: '',
  taxYear: '',
  taxId: '',
  sendToWebsite: '',
  rollToNextYear: '',
  addToLegal: '',
  comment: '',
  category: '',
  userName: '',
  createdAt: '',
  updatedAt: '',
  reasonCode: '',
  beforeChange: '',
  afterChange: '',
  taxInquiryTransaction: {
    _id: '',
    taxYear: '',
    name1: '',
    name2: '',
    address1: '',
    taxId: '',
  },
};

// taxCommentTypesObj.ownerInfoCode. Not imported from the tax components folder so the
// store does not reach up into a component tree for a constant.
const OWNER_INFO_CATEGORY = 3;

// Deliberately not defaultTransactionFields: the owner-history grid is read-only and needs
// wkstNumber and ownerChanges, which the Comments tab does not ask for and must not start
// echoing back on an edit.
const ownerHistoryFields = {
  _id: '',
  taxYear: '',
  taxId: '',
  category: '',
  comment: '',
  beforeChange: '',
  afterChange: '',
  ownerChanges: { field: '', from: '', to: '' },
  userName: '',
  wkstNumber: '',
  // createdAt with the clock time kept - plain createdAt is a GraphQLDate and loses it.
  changedOn: '',
};

export const taxComments = {
  namespaced: true,
  state: () => ({
    isDialog: false,
    comments: [],
    criteria: [],
    selectedItems: [],
    commentsCategory: [],
  }),
  mutations: {
    setComments(state, values) {
      state.comments = values;
    },
    setCriteria(state, values) {
      state.criteria = values;
    },
    setSelectedItems(state, items) {
      state.selectedItems = items;
    },
    setCommentsCategory(state, category) {
      state.commentsCategory = category;
    },
  },
  getters: {
    commentsRows: (state) => state.comments,
    selectedCriteria: (state) => state.criteria,
  },
  actions: {
    async loadTaxComments({ commit, dispatch }, { criteria, baseCrudKey, postProcessTrans }) {
      let results;

      try {
        const gqlfields = defaultTransactionFields;

        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`
          query taxComments($criteria:taxCommentsCriteriaType){
              taxComments(criteria: $criteria) {
                ${fieldsToGqlString(gqlfields)}
              }
            }
          `,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });

        clients.handleGQLErrors(results, commit);
        if (results.data) {
          const taxCommentTrans = results.data.taxComments || [];
          const processedTransData = postProcessTrans
            ? postProcessTrans(taxCommentTrans) : taxCommentTrans;
          commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
          return taxCommentTrans;
        }
      } catch (error) {
        dispatch('logException', error, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem while loading Tax Comment List!', { root: true });
      }
      return [];
    },
    async upsertTaxCommentTrans({ dispatch, rootGetters, commit },
      { item, baseCrudKey, postProcessTrans }) {
      let results;

      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertTaxComments($item:taxCommentMutationInputType!) {
            upsertTaxComments(upsert:$item) {
              ${(fieldsToGqlString(defaultTransactionFields))}
            }
          }`,
          variables: {
            item,
          },
        });

        clients.handleGQLErrors(results, commit);
        const taxData = rootGetters['base/crud/items'](baseCrudKey);
        const newTaxComment = results.data ? (results.data.upsertTaxComments || {}) : {};
        const newList = mergeByIdNewAtTop(taxData || [], newTaxComment);
        const processedTransData = postProcessTrans
          ? postProcessTrans(newList) : newList;
        commit('base/crud/setItems', [baseCrudKey, processedTransData], { root: true });
        return newTaxComment;
      } catch (error) {
        dispatch('logException', error, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem while upserting the tax comment!', { root: true });
      }
      return {};
    },
    // Owner-info (category 3) comments for a whole tax record - every year of it - for the
    // History tab's Owner History view. `taxRecords` is the list of (taxYear, taxId) pairs
    // the tab already loaded; taxId on its own is not enough, because the same taxId belongs
    // to a different parcel in an earlier year (see commentFilter on the backend).
    async loadOwnerChangeHistory({ commit, dispatch }, { taxRecords, baseCrudKey }) {
      let results;
      if (!(taxRecords || []).length) {
        if (baseCrudKey) commit('base/crud/setItems', [baseCrudKey, []], { root: true });
        return [];
      }
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`
          query taxComments($criteria:taxCommentsCriteriaType){
              taxComments(criteria: $criteria) {
                ${fieldsToGqlString(ownerHistoryFields)}
              }
            }
          `,
          variables: {
            criteria: {
              taxRecords: taxRecords.map(({ taxYear, taxId }) => ({ taxYear, taxId })),
              category: [OWNER_INFO_CATEGORY],
              sortBy: [{ column: 'createdAt', direction: 'desc' }],
            },
          },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        const rows = (results.data && results.data.taxComments) || [];
        if (baseCrudKey) commit('base/crud/setItems', [baseCrudKey, rows], { root: true });
        return rows;
      } catch (error) {
        dispatch('logException', error, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem while loading the owner change history!', { root: true });
      }
      return [];
    },
    // When the county's owner change history starts - a converted county has none before it.
    async loadOwnerChangeHistoryStartDate({ commit, dispatch }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query ownerChangeHistoryStartDate { ownerChangeHistoryStartDate }`,
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        return (results.data && results.data.ownerChangeHistoryStartDate) || null;
      } catch (error) {
        // Only drives an empty-state caption; a failure here should not flash at the user.
        dispatch('logException', error, { root: true });
      }
      return null;
    },
    async bulkUpsertComments({ dispatch, commit },
      { item }) {
      let results;

      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation bulkUpsertComments($item:[taxCommentMutationInputType]) {
            bulkUpsertComments(upserts:$item) {
              ${(fieldsToGqlString(defaultTransactionFields))}
            }
          }`,
          variables: {
            item,
          },
        });
        dispatch('flashSuccess', 'comment has been aadded Successfully', { root: true });
      } catch (error) {
        dispatch('logException', error, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem while upserting the tax comment!', { root: true });
      }
      return {};
    },
    async deleteTaxComments({ commit, rootGetters, dispatch }, { item, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation deleteTaxComments ($id: ID!)  {
                deleteTaxComments(id: $id) {
                      _id
                    }
                  }`,
          variables: {
            id: item._id,
          },
        });
        clients.handleGQLErrors(results, commit);
        const delId = results.data.deleteTaxComments._id;
        const taxData = rootGetters['base/crud/items'](baseCrudKey);
        const updatedData = taxData.filter((d) => d._id !== delId);
        commit('base/crud/setItems', [baseCrudKey, updatedData], { root: true });
        commit('base/crud/setIsVisible', [baseCrudKey, false], { root: true });
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem deleting the List', { root: true });
      }
    },
  },
};

export default taxComments;
